import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import ChartCard from './ChartCard';
import ChartLegend from './ChartLegend';
import { ChartTooltipBox } from './ChartTooltip';
import { CHART_SURFACE, SERIES_COLORS } from './chartTheme';
import { formatPercent } from './formatters';
import { DeclineReason, OTHER_REASONS_KEY } from '../../utils/statistics';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';

interface DeclineReasonsChartProps {
  reasons: DeclineReason[];
}

const DeclineReasonsChart = ({ reasons }: DeclineReasonsChartProps) => {
  const { t } = useTranslation();

  const total = useMemo(
    () => reasons.reduce((sum, reason) => sum + reason.count, 0),
    [reasons]
  );

  const data = useMemo(
    () =>
      reasons.map((reason, index) => ({
        ...reason,
        label:
          reason.key === OTHER_REASONS_KEY
            ? t(COMMON_TRANSLATE_KEYS.STATS_OTHER_REASONS)
            : t(reason.key),
        color: SERIES_COLORS[index % SERIES_COLORS.length],
      })),
    [reasons, t]
  );

  const pieData = useMemo(() => data.filter((item) => item.count > 0), [data]);

  return (
    <ChartCard
      title={t(COMMON_TRANSLATE_KEYS.STATS_DECLINE_REASONS)}
      subtitle={`${t(COMMON_TRANSLATE_KEYS.STATS_DECLINED)}: ${total}`}
    >
      {total === 0 ? (
        <p className="text-textGray text-sm py-10 text-center">
          {t(COMMON_TRANSLATE_KEYS.STATS_NO_DECLINES)}
        </p>
      ) : (
        <div className="flex sm:flex-row flex-col gap-6 items-center">
          <div className="h-[200px] w-[200px] flex-shrink-0 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="count"
                  nameKey="label"
                  innerRadius={60}
                  outerRadius={95}
                  stroke={CHART_SURFACE}
                  strokeWidth={2}
                  isAnimationActive={false}
                >
                  {pieData.map((item) => (
                    <Cell key={item.key} fill={item.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) {
                      return null;
                    }

                    const item = payload[0].payload;

                    return (
                      <ChartTooltipBox
                        title={item.label}
                        rows={[
                          {
                            key: item.key,
                            label: t(COMMON_TRANSLATE_KEYS.STATS_DECLINED),
                            color: item.color,
                            value: `${item.count} (${formatPercent(
                              item.count,
                              total
                            )}%)`,
                          },
                        ]}
                      />
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-mainWhite text-2xl font-semibold">
                {total}
              </span>
            </div>
          </div>
          <div className="w-full min-w-0">
            <ChartLegend
              direction="column"
              items={data.map((item) => ({
                key: item.key,
                label: item.label,
                color: item.color,
                value: `${item.count} · ${formatPercent(item.count, total)}%`,
              }))}
            />
          </div>
        </div>
      )}
    </ChartCard>
  );
};

export default DeclineReasonsChart;
